import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import customAxios from "../../../utils/customAxios";
import AdminPage from "../_components/AdminPage";
import AdminTable, { cellClass, stickyCellClass } from "../_components/AdminTable";
import AdminBadge from "../_components/AdminBadge";
import AdminRowActions, { ViewLink } from "../_components/AdminRowActions";
import AdminFilterBar, { DateRangeField } from "../_components/AdminFilterBar";
import { BackAltIcon } from "../../icons";

/**
 * The schedule for one driver or one vehicle: every booking they are assigned
 * to, in pick-up order. Reached from the row action on the driver and vehicle
 * lists.
 *
 * One page serves both because the two schedules differ only in which resource
 * is fixed and which one the rows show alongside it.
 */

const RESOURCES = {
  driver: {
    title: "Driver Schedule",
    listPath: "/admin/driverlist",
    listLabel: "Back to drivers",
    detailUrl: (id) => `/api/admin/drivers/${id}`,
    queryKey: "driverId",
    // A driver's schedule is about which car they are in.
    pairColumn: { key: "vehicle", title: "Vehicle", width: "tw:w-[240px]", skeleton: "stacked" },
    renderPair: (row) => ({ primary: row.vehicleName, secondary: row.registrationNumber }),
    describe: (item) => ({
      name: item?.name || "-",
      lines: [item?.email, item?.phone].filter(Boolean),
    }),
  },
  vehicle: {
    title: "Vehicle Schedule",
    listPath: "/admin/vehiclelist",
    listLabel: "Back to vehicles",
    detailUrl: (id) => `/api/admin/vehicles/${id}`,
    queryKey: "vehicleId",
    // A vehicle's schedule is about who is driving it.
    pairColumn: { key: "driver", title: "Driver", width: "tw:w-[240px]", skeleton: "stacked" },
    renderPair: (row) => ({ primary: row.driverName, secondary: row.driverPhone }),
    describe: (item) => ({
      name: item?.vehiclename || "-",
      lines: [item?.vehicletype, item?.registration].filter(Boolean),
    }),
  },
};

const BOOKING_STATUS_TONES = {
  CONFIRMED: "success",
  READY_TO_CHECKOUT: "info",
  PAYMENT_PENDING: "info",
  INQUIRY: "warning",
};

const humanize = (value) =>
  value
    ? value
        .toLowerCase()
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
    : "-";

const getBookingTypeLabel = (bookingType, isAirportTransfer) => {
  if (bookingType === "byTheHour") return "By The Hour";
  if (isAirportTransfer) return "Airport Transfer";
  return "Specified Destination";
};

const DateTimeCell = ({ value }) => {
  if (!value) return <span>-</span>;
  const date = moment(value).locale("en");

  return (
    <div className="tw:flex tw:flex-col">
      <span className="tw:whitespace-nowrap">
        {date.format("YYYY/MM/DD")} <span className="tw:!text-[13px] tw:!text-[#8D8D8D]">{date.format("(ddd)")}</span>
      </span>
      <span>{date.format("H:mm")}</span>
    </div>
  );
};

const StackedCell = ({ primary, secondary }) => (
  <div className="tw:flex tw:flex-col">
    <span className="tw:!text-[15px] tw:!text-[#282828] tw:leading-none tw:mb-1">{primary || "-"}</span>
    <span className="tw:!text-[13px] tw:!text-[#8D8D8D] tw:leading-none">{secondary || "-"}</span>
  </div>
);

const ResourceSchedulePage = ({ resource, match }) => {
  const config = RESOURCES[resource];
  const resourceId = match?.params?.id;

  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Opens on what is still to come rather than the whole history.
  const [dateRange, setDateRange] = useState({
    startDate: moment().startOf("day"),
    endDate: moment().add(3, "months").endOf("day"),
  });

  const columns = [
    { key: "no", title: "#", width: "tw:w-[60px] tw:rounded-tl-[5px]", skeleton: "narrow" },
    { key: "pickup", title: "Pick-up", width: "tw:w-[170px]" },
    { key: "dropoff", title: "Drop-off", width: "tw:w-[170px]" },
    { key: "bookingId", title: "Booking ID", width: "tw:w-[150px]" },
    { key: "customer", title: "Customer", width: "tw:w-[200px]" },
    { key: "bookingType", title: "Transfer Type", width: "tw:w-[190px]" },
    { key: "route", title: "Route", width: "tw:w-[300px]", skeleton: "stacked" },
    config.pairColumn,
    { key: "status", title: "Booking Status", width: "tw:w-[180px]", skeleton: "badge" },
    { key: "action", title: "Action", width: "tw:w-[120px] tw:rounded-tr-[5px]", sticky: true, align: "center" },
  ];

  const fetchSchedule = useCallback(async () => {
    if (!resourceId) return;
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams({
        [config.queryKey]: resourceId,
        startDate: moment(dateRange.startDate).format("YYYY-MM-DDTHH:mm:ss[Z]"),
        endDate: moment(dateRange.endDate).format("YYYY-MM-DDTHH:mm:ss[Z]"),
      });

      const [scheduleResponse, detailResponse] = await Promise.all([
        customAxios.get(`/api/admin/bookings/schedule/list?${params.toString()}`),
        customAxios.get(config.detailUrl(resourceId)).catch(() => null),
      ]);

      setRows(scheduleResponse?.data?.data || []);
      if (detailResponse?.data) setDetail(detailResponse.data);
    } catch (err) {
      setError(err?.response?.data?.msg || "Could not load the schedule.");
      setRows([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resourceId, resource, dateRange]);

  useEffect(() => {
    fetchSchedule();
  }, [fetchSchedule]);

  const described = config.describe(detail);

  return (
    <AdminPage title={config.title}>
      <Link
        to={config.listPath}
        className="tw:mt-[24px] tw:inline-flex tw:items-center tw:gap-2 tw:text-[14px] tw:!text-[#7C7B7B] tw:!no-underline"
      >
        <BackAltIcon />
        {config.listLabel}
      </Link>

      <div className="tw:mt-[16px] tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-4 tw:rounded-[5px] tw:border tw:border-[#E6E6E6] tw:bg-white tw:px-[24px] tw:py-[20px]">
        <div>
          <div className="tw:text-[18px] tw:font-semibold tw:leading-[normal] tw:!text-[#282828]">
            {described.name}
          </div>
          {described.lines.length ? (
            <div className="tw:mt-1 tw:text-[14px] tw:leading-[20px] tw:!text-[#7C7B7B]">
              {described.lines.join(" · ")}
            </div>
          ) : null}
        </div>
        <div className="tw:text-[14px] tw:leading-[20px] tw:!text-[#7C7B7B]">
          {loading ? "Loading…" : `${rows.length} booking${rows.length === 1 ? "" : "s"} in this period`}
        </div>
      </div>

      {/* The period is the whole filter here, so it stays open with no toggle. */}
      <AdminFilterBar columns={4} collapsible={false}>
        <DateRangeField label="Pick-up Date・Period" dateRange={dateRange} onChange={setDateRange} />
      </AdminFilterBar>

      {error ? (
        <div className="tw:mt-[16px] tw:text-[14px] tw:!text-[#EA4335]">{error}</div>
      ) : null}

      <AdminTable
        columns={columns}
        minWidth={1880}
        loading={loading}
        skeletonRowCount={8}
        isEmpty={!rows.length}
        emptyText="No bookings assigned in this period."
      >
        {rows.map((row, index) => {
          const pair = config.renderPair(row);
          const editPath =
            row.dataVersion === "v2" || row.dataVersion === "v3"
              ? `/admin/bookinglist/edit/v2/${row._id}`
              : `/admin/bookinglist/edit/${row._id}`;

          return (
            <tr key={`${row._id}-${row.vehicleSlot}`}>
              <td className={cellClass}>{index + 1}</td>
              <td className={`${cellClass} tw:whitespace-nowrap`}>
                <DateTimeCell value={row.pickupDateTime} />
              </td>
              <td className={`${cellClass} tw:whitespace-nowrap`}>
                <DateTimeCell value={row.dropoffDateTime} />
              </td>
              <td className={cellClass}>{`#${row.key}`}</td>
              <td className={cellClass}>{row.customerName}</td>
              <td className={cellClass}>{getBookingTypeLabel(row.bookingType, row.isAirportTransfer)}</td>
              <td className={cellClass}>
                <div className="tw:flex tw:flex-col">
                  <span className="tw:!text-[15px] tw:!text-[#282828] tw:leading-none tw:mb-1 tw:block tw:max-w-[280px] tw:truncate">
                    {row.pickupLocation || "-"}
                  </span>
                  <span className="tw:!text-[13px] tw:!text-[#8D8D8D] tw:leading-none tw:block tw:max-w-[280px] tw:truncate">
                    {row.destination ? `→ ${row.destination}` : "—"}
                  </span>
                </div>
              </td>
              <td className={cellClass}>
                <StackedCell primary={pair.primary} secondary={pair.secondary} />
              </td>
              <td className={cellClass}>
                <AdminBadge tone={BOOKING_STATUS_TONES[row.bookingStatus] || "neutral"}>
                  {humanize(row.bookingStatus)}
                </AdminBadge>
              </td>
              <td className={stickyCellClass}>
                <AdminRowActions>
                  <ViewLink to={editPath} label="View booking" />
                </AdminRowActions>
              </td>
            </tr>
          );
        })}
      </AdminTable>
    </AdminPage>
  );
};

export default ResourceSchedulePage;
